import React, { useMemo, useState, useCallback, useRef } from 'react'
import { Task, TaskStatus, Supplier } from '../../types/mockup'
import { TaskItem } from './TaskItem'
import { DependencyLines } from './DependencyLines'

interface ScheduleListProps {
  tasks: Task[]
  suppliers: Supplier[]
  onTaskClick?: (task: Task) => void
  selectedTaskId?: string
  onTaskUpdate?: (taskId: string, updates: Partial<Task>) => void
  onTaskDrop?: (draggedTaskId: string, targetTaskId: string) => void
  showCompleted?: boolean
  groupBySupplier?: boolean
  currentTime?: Date
  showDependencies?: boolean
}

export const ScheduleList: React.FC<ScheduleListProps> = ({
  tasks,
  suppliers,
  onTaskClick,
  selectedTaskId,
  onTaskUpdate,
  onTaskDrop,
  showCompleted = true,
  groupBySupplier = false,
  currentTime = new Date(),
  showDependencies = true
}) => {
  const [draggedTaskId, setDraggedTaskId] = useState<string | null>(null)
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())
  const containerRef = useRef<HTMLDivElement>(null)

  const filteredTasks = useMemo(() => {
    let filteredTasks = [...tasks]
    
    if (!showCompleted) {
      filteredTasks = filteredTasks.filter(task => task.status !== 'complete')
    }
    
    filteredTasks.sort((a, b) => a.startTime.getTime() - b.startTime.getTime())
    
    return filteredTasks
  }, [tasks, showCompleted])

  const groupedTasks = useMemo(() => {
    if (!groupBySupplier) return { 'All Tasks': filteredTasks }
    
    const groups: Record<string, Task[]> = {}
    
    filteredTasks.forEach(task => {
      const supplierName = task.supplier?.name || 'Unassigned'
      if (!groups[supplierName]) {
        groups[supplierName] = []
      }
      groups[supplierName].push(task)
    })
    
    return groups
  }, [filteredTasks, groupBySupplier])

  const getCurrentTask = useCallback((): Task | undefined => {
    return filteredTasks.find(task => {
      const now = currentTime.getTime()
      return task.startTime.getTime() <= now && task.endTime.getTime() > now
    })
  }, [filteredTasks, currentTime])

  const getNextTask = useCallback((): Task | undefined => {
    const now = currentTime.getTime()
    return filteredTasks.find(task => task.startTime.getTime() > now)
  }, [filteredTasks, currentTime])


  const handleTaskClick = (task: Task) => {
    onTaskClick?.(task)
  }

  const handleStatusChange = (taskId: string, status: TaskStatus) => {
    onTaskUpdate?.(taskId, { status })
  }

  const handleQuickAction = (taskId: string, action: 'delay5' | 'delay10' | 'notify') => {
    const task = tasks.find(t => t.id === taskId)
    if (!task) return
    
    switch (action) {
      case 'delay5':
        onTaskUpdate?.(taskId, { delayMinutes: (task.delayMinutes || 0) + 5 })
        break
      case 'delay10':
        onTaskUpdate?.(taskId, { delayMinutes: (task.delayMinutes || 0) + 10 })
        break
      case 'notify':
        console.log('Send notification for task:', task.name)
        break
    }
  }

  const relatedTasksMap = useMemo(() => {
    const map = new Map<string, string>()
    tasks.forEach(task => {
      map.set(task.id, task.name)
    })
    return map
  }, [tasks])

  const handleDragStart = (e: React.DragEvent, taskId: string) => {
    const task = tasks.find(t => t.id === taskId)
    if (task && !task.isAnchor) {
      setDraggedTaskId(taskId)
      e.dataTransfer.effectAllowed = 'move'
    }
  }

  const handleDragEnd = () => {
    setDraggedTaskId(null)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  const handleDrop = (e: React.DragEvent, targetTaskId: string) => {
    e.preventDefault()
    if (draggedTaskId && draggedTaskId !== targetTaskId) {
      onTaskDrop?.(draggedTaskId, targetTaskId)
    }
    setDraggedTaskId(null)
  }

  const toggleGroup = (groupName: string) => {
    const newExpanded = new Set(expandedGroups)
    if (newExpanded.has(groupName)) {
      newExpanded.delete(groupName)
    } else {
      newExpanded.add(groupName)
    }
    setExpandedGroups(newExpanded)
  }

  const renderTask = (task: Task) => {
    const isCurrentTask = getCurrentTask()?.id === task.id
    const isNextTask = getNextTask()?.id === task.id
    const isDragging = draggedTaskId === task.id
    const isSelected = selectedTaskId === task.id

    const supplierData = suppliers.find(s => s.id === task.supplierId)
    const taskWithSupplier = { ...task, supplier: supplierData }
    
    return (
      <TaskItem
        key={task.id}
        task={taskWithSupplier}
        isSelected={isSelected}
        isCurrent={isCurrentTask}
        isNext={isNextTask}
        isDragging={isDragging}
        onSelect={handleTaskClick}
        onStatusChange={handleStatusChange}
        onQuickAction={handleQuickAction}
        onDragStart={(e) => handleDragStart(e, task.id)}
        onDragEnd={handleDragEnd}
        onDragOver={handleDragOver}
        onDrop={(e, dropTask) => handleDrop(e, dropTask.id)}
        showDependencies={showDependencies}
        relatedTasks={relatedTasksMap}
      />
    )
  }

  return (
    <div className="schedule-list" ref={containerRef} style={{ position: 'relative' }}>
      {showDependencies && (
        <DependencyLines 
          tasks={filteredTasks} 
          containerRef={containerRef}
        />
      )}
      <div className="schedule-list-header">
        <h3>Schedule Timeline</h3>
        <div className="schedule-controls">
          <button 
            className={`control-btn ${!showCompleted ? 'active' : ''}`}
            onClick={() => {}}
          >
            Hide Complete
          </button>
          <button 
            className={`control-btn ${groupBySupplier ? 'active' : ''}`}
            onClick={() => {}}
          >
            Group by Supplier
          </button>
        </div>
      </div>
      
      <div className="schedule-list-content">
        {Object.entries(groupedTasks).map(([groupName, tasks]) => (
          <div key={groupName} className="task-group">
            {groupBySupplier && (
              <div 
                className="task-group-header"
                onClick={() => toggleGroup(groupName)}
              >
                <span className="group-toggle">
                  {expandedGroups.has(groupName) ? '▼' : '▶'}
                </span>
                <span className="group-name">{groupName}</span>
                <span className="group-count">{tasks.length} tasks</span>
              </div>
            )}
            
            {(!groupBySupplier || expandedGroups.has(groupName)) && (
              <div className="task-group-items">
                {(!groupBySupplier || expandedGroups.has(groupName)) && 
                  tasks.map(renderTask)
                }
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

export default ScheduleList